import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '@/services/axiosClient';

export type TaskStatus = 'assigned' | 'in_progress' | 'completed' | 'expired';
export type TaskPriority = 'high' | 'normal' | 'low';

export interface Task {
    id?: string;
    taskId?: string;
    subject: string;
    associatedName?: string;
    associatedId?: string | null;
    associatedType?: 'lead' | 'ticket' | 'prospect' | 'order';
    priority: TaskPriority;
    status: TaskStatus;
    dueDate: string;        // ISO Date String (e.g., "2026-06-28")
    assignedBy?: string;
    assignedToEmail?: string;
    AssignedToEmail?: string;
    order?: {
        orderId: string;
        displayReference: string;
        url: string;
    };
    creator?: {
        userId: string | null;
        displayName: string;
        email: string;
    } | null;
    assignee?: {
        userId: string | null;
        displayName: string;
        email: string;
        isActive?: boolean | null;
    } | null;
    version?: number;
    createdAt?: string;
    updatedAt?: string;
}

export const TasksPage: React.FC = () => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // Routes through the shared axios client: baseURL '/api' → Vite proxy
        // strips '/api' → Gateway → analytics service. Auth token is injected
        // by the axiosClient request interceptor.
        axiosClient.get<Task[] | { items: Task[] }>('/tasks')
            .then(res => {
                setTasks(Array.isArray(res.data) ? res.data : res.data.items);
            })
            .catch(err => {
                console.error("Error loading tasks page data:", err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [priorityFilter, setPriorityFilter] = useState<string>('all');

    const filteredTasks = tasks.filter((task) => {
        const matchesSearch = (task.subject?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (task.associatedName?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (task.order?.displayReference.toLowerCase() || '').includes(search.toLowerCase()) ||
            (task.assignee?.displayName.toLowerCase() || '').includes(search.toLowerCase()) ||
            (task.assignedToEmail?.toLowerCase() || task.AssignedToEmail?.toLowerCase() || '')
                .includes(search.toLowerCase());
        const todayStr = new Date().toISOString().split('T')[0];

        // 1. If it's explicitly completed, it can NEVER be past due or expired
        const isCompleted = task.status?.toLowerCase() === 'completed';
        const isPastDue = !isCompleted && task.dueDate && task.dueDate !== 'N/A' && task.dueDate < todayStr;

        // 2. Strict category router
        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'completed' ? isCompleted :
                statusFilter === 'expired' ? (task.status?.toLowerCase() === 'expired' || isPastDue) :
                    statusFilter === 'assigned' ? (task.status?.toLowerCase() === 'assigned' && !isPastDue) :
                        task.status?.toLowerCase() === statusFilter.toLowerCase());

        const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">My Tasks</h1>
                    <p className="text-sm text-gray-500 mt-1">Manage and track your action items</p>
                </div>
            </div>

            {/* Filter Ribbon Control Panel */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-wrap gap-4 items-center">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                    <input
                        type="text"
                        placeholder="Search Tasks..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                </div>

                {/* Status Selector */}
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg p-2 text-sm bg-white"
                    >
                        <option value="all">All Statuses</option>
                        <option value="assigned">Assigned / Created</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed (Old)</option>
                        <option value="expired">Expired (Old)</option>
                    </select>
                </div>

                {/* Priority Selector */}
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Priority:</span>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="border border-gray-300 rounded-lg p-2 text-sm bg-white"
                    >
                        <option value="all">All Priorities</option>
                        <option value="high">High</option>
                        <option value="normal">Normal</option>
                        <option value="low">Low</option>
                    </select>
                </div>
            </div>

            {/* Actual Live Data Table Layout */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="bg-gray-50 font-semibold text-gray-700">
                    <tr>
                        <th className="p-4">Task ID</th>
                        <th className="p-4">Subject</th>
                        <th className="p-4">Associated Entity</th>
                        <th className="p-4">Priority</th>
                        <th className="p-4">Assignee</th>
                        <th className="p-4">Status</th>
                        <th className="p-4">Due Date</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 text-gray-600">
                    {isLoading ? (
                        <tr>
                            <td colSpan={7} className="p-8 text-center text-gray-400">
                                Loading your dashboard task records...
                            </td>
                        </tr>
                    ) : filteredTasks.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="p-8 text-center text-gray-400">
                                No tasks matched your criteria.
                            </td>
                        </tr>
                    ) : (
                        filteredTasks.map((task) => (
                            <tr key={task.taskId ?? task.id} className="hover:bg-gray-50 transition-colors">
                                <td className="p-4 font-medium text-purple-600 truncate max-w-[120px]" title={task.taskId ?? task.id}>
                                    {task.taskId ?? task.id}
                                </td>
                                <td className="p-4 text-gray-900 font-medium">{task.subject}</td>
                                <td className="p-4">
                                    <span className="text-xs uppercase bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2 font-bold">
                                      {task.order ? 'order' : task.associatedType}
                                    </span>
                                    {task.order ? (
                                        <Link
                                            to={`/orders/${task.order.orderId}?tab=tasks-actions`}
                                            className="font-medium text-brand-accent hover:underline"
                                        >
                                            {task.order.displayReference}
                                        </Link>
                                    ) : task.associatedName}
                                </td>

                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                        task.priority === 'high' ? 'bg-red-100 text-red-800' :
                                            task.priority === 'normal' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                                    }`}>
                                      {task.priority}
                                    </span>
                                </td>

                                {/* ADDED THIS CELL BLOCK */}
                                <td className="p-4 text-gray-700 font-medium break-all">
                                    {task.assignee?.displayName
                                        || task.assignedToEmail
                                        || task.AssignedToEmail
                                        || <span className="text-gray-400 italic">Unassigned</span>}
                                </td>
                                {/* END OF NEW CELL BLOCK */}

                                <td className="p-4">
                                    <span className="capitalize">{task.status.replace('_', ' ')}</span>
                                </td>
                                <td className="p-4">
                                    {(() => {
                                        const todayStr = new Date().toISOString().split('T')[0];
                                        const isPastDue = task.dueDate && task.dueDate !== 'N/A' && task.dueDate < todayStr && task.status !== 'completed';

                                        return (
                                            <span className={isPastDue ? "text-red-600 font-semibold" : "text-gray-500"}>
                {task.dueDate || 'N/A'}
            </span>
                                        );
                                    })()}
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TasksPage;
